import React from 'react';
import { Edit, Trash2, DollarSign, EyeOff } from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  category: string;
  is_active: boolean;
  hidden: boolean;
  duration_hours?: number;
  skill_level?: string;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
}

interface CourseCardProps {
  course: Course;
  onEdit: (course: Course) => void;
  onDelete: (courseId: string) => void;
}

export default function CourseCard({ course, onEdit, onDelete }: CourseCardProps) {
  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-purple-500/50 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-bold text-white">{course.title}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              course.is_active 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>
              {course.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
              {course.category}
            </span>
            {course.hidden && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-600/20 text-gray-400 border border-gray-600/30 flex items-center space-x-1">
                <EyeOff className="w-3 h-3" />
                <span>Hidden</span>
              </span>
            )}
          </div>
          <p className="text-gray-400 mb-4">{course.description}</p>
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-white font-semibold">${course.full_price}</span>
              {course.discount_price && (
                <span className="text-green-400">→ ${course.discount_price}</span>
              )}
            </div>
            {course.duration_hours && (
              <span className="text-gray-400">{course.duration_hours}h</span>
            )}
            {course.skill_level && (
              <span className="text-gray-400">{course.skill_level}</span>
            )}
          </div>
        </div>
        <div className="flex space-x-2 ml-4">
          <button
            onClick={() => onEdit(course)}
            className="p-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg transition-colors"
            title="Edit course"
          >
            <Edit className="w-5 h-5 text-blue-400" />
          </button>
          <button
            onClick={() => onDelete(course.id)}
            className="p-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg transition-colors"
            title="Delete course"
          >
            <Trash2 className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
